import {
  ConsultantBookingStatus,
  InitialContact,
  useGetPersonalInfoCardDataQuery
} from "@bonliva-crm/graphql";
import {
  CallIcon,
  Color,
  EditFilledIcon,
  FavoriteIcon,
  HomeIcon,
  Loader,
  MailIcon,
  VerifiedIcon
} from "@bonliva-crm/ui";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import moment from "moment";
import cn from "classnames";
import { transparentize } from "polished";
import { useState } from "react";
import { professions } from "@bonliva-crm/common";
import { ConsultantTags } from "../consultant-tags/consultant-tags";
import { LeadStatusModal } from "../lead-status/lead-status-modal";
import { Item } from "./components/item";
import { KarensModal } from "./components/karens";
import { QualityApprovalTooltip } from "./quality-approval-tooltip";
import { NationalQualityStatusIcon } from "./national-quality-status-icon";
import { PipelineStatus } from "../pipeline-status/pipeline-status";
import { useLocale } from "@hooks";
import { ResponsiblePersonItem } from "./components/responsible-person-item";
import { WorkStatusSection } from "./components/work-status-section";
import SmartphoneIcon from "@material-ui/icons/Smartphone";
import { ConsultantFavouriteToggle } from "../../consultant-favourites/consultant-favourite-toggle";

type Props = {
  consultantId: string;
  onEdit: () => void;
};

const getInitials = (name?: string | null) => {
  if (!name) return "??";
  return name
    .split(" ")
    .map((v) => v.charAt(0).toUpperCase())
    .join("");
};

const dateFormat = "YYYY-MM-DD";

export function PersonalInfoCard({ consultantId, onEdit }: Props) {
  const classes = useStyles();
  const { locale } = useLocale();
  const [openKarensModal, setOpenKarensModal] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);

  const {
    data: { consultantById },
    loading
  } = useGetPersonalInfoCardDataQuery({
    fetchPolicy: "cache-first",
    variables: {
      id: consultantId
    }
  });

  const contactInfoSection = (
    <>
      <Box className={classes.avatarContainer}>
        <Avatar className={classes.avatar}>
          <Typography variant="h5">{getInitials(consultantById.Name)}</Typography>
        </Avatar>
        <Box>
          <Item
            canCopy
            value={consultantById.Name}
            valueType="h5"
            iconRight={
              consultantById.initialContact === InitialContact.SelfRegister && (
                <FavoriteIcon
                  htmlColor={Color.PrimaryMain}
                  style={{
                    verticalAlign: "bottom",
                    width: "20px",
                    marginLeft: "4px"
                  }}
                />
              )
            }
          />
          <Typography variant="body2" color="textSecondary">
            {consultantById.LastContactDate &&
              `${locale.LastContact}: ${moment(consultantById.LastContactDate).format(
                "YY-MM-DD"
              )}`}
          </Typography>
        </Box>
        <Box
          style={{
            marginLeft: "auto",
            marginBottom: "auto",
            display: "flex",
            alignItems: "flex-start"
          }}
        >
          <ConsultantFavouriteToggle consultantId={consultantId} size="small" />
          <IconButton
            style={{
              padding: "6px"
            }}
            onClick={() => onEdit()}
          >
            <EditFilledIcon />
          </IconButton>
        </Box>
      </Box>
      <Box className={classes.container}>
        <Item
          value={consultantById.Email}
          valueType="email"
          iconLeft={<MailIcon htmlColor={Color.Body} className={classes.itemIcon} />}
          canCopy
        />
        <Item
          value={consultantById.secondaryEmail}
          valueType="email"
          iconLeft={<MailIcon htmlColor={Color.Body} className={classes.itemIcon} />}
          canCopy
        />
        <Item
          value={consultantById.CellPhone}
          valueType="phone"
          iconLeft={
            <SmartphoneIcon
              htmlColor={Color.Body}
              fontSize={"small"}
              className={classes.itemIcon}
            />
          }
          canCopy
        />
        <Item
          value={consultantById.Phone}
          valueType="phone"
          iconLeft={<CallIcon htmlColor={Color.Body} className={classes.itemIcon} />}
          canCopy
        />
        <Item
          value={consultantById.Address?.formattedShort}
          iconLeft={<HomeIcon htmlColor={Color.Body} className={classes.itemIcon} />}
          canCopy
        />
      </Box>
    </>
  );

  const personalInfoSection = (
    <>
      <Divider className={classes.divider} />
      <Box className={classes.container}>
        <Item
          value={consultantById.SocialSecurityNo}
          title={locale.SocialSecurityNoShort}
          canCopy
        />
        <ResponsiblePersonItem
          responsiblePerson={consultantById.ResponsiblePerson}
          consultantId={consultantById._id}
        />
        <Item
          value={consultantById.preferredCommunication
            .map((c) => locale.preferredCommunication[c.toLowerCase()])
            .join(", ")}
          title={locale["Preferable communication"]}
        />
      </Box>
    </>
  );

  const professionInfoSection = (
    <>
      <Divider className={classes.divider} />
      <Typography variant="body1" color="textSecondary" className={classes.sectionTitle}>
        {locale.ProfessionInformation}
      </Typography>
      <Box className={classes.container}>
        <Item
          title={locale.AreaOfExpertise}
          value={consultantById.AreasOfExpertise.join(", ")}
        />
        <Item
          title={locale.Specialization}
          value={consultantById.Specializations.join(", ")}
        />
        <Item
          title={locale.PrescriptionCode}
          value={consultantById.PrescriptionCode}
          canCopy
        />
        <Item
          title={locale["Known systems"]}
          value={consultantById.expertise.systems.join(", ") || " "}
        />
        {consultantById.expertise.machines.length > 0 && (
          <Item
            title={locale.Machines}
            value={consultantById.expertise.machines.join(", ") || " "}
          />
        )}
        {consultantById.expertise.methods.length > 0 && (
          <Item
            title={locale.Methods}
            value={consultantById.expertise.methods.join(", ") || " "}
          />
        )}
        {consultantById.expertise.educations.length > 0 && (
          <Item
            title={locale.Educations}
            value={consultantById.expertise.educations.join(", ")}
          />
        )}
        <Item
          title={locale["Employment type"]}
          value={
            consultantById.LegalPersonType
              ? locale.legalPersonType[consultantById.LegalPersonType]
              : undefined
          }
        />
        {consultantById.hasDrivingLicence && (
          <Item
            title={locale.HasDrivingLicence}
            value={
              consultantById.drivingLicenceType
                ? locale.drivingLicenceType[consultantById.drivingLicenceType]
                : locale.Yes
            }
          />
        )}
      </Box>
    </>
  );

  const bookingStatusSection = (
    <>
      <Divider className={classes.divider} />
      <Typography variant="body1" color="textSecondary" className={classes.sectionTitle}>
        {locale.BookingStatus}
      </Typography>
      <Box className={classes.container}>
        <Typography variant="h6" component="div">
          {locale.activeContract}
        </Typography>
        <Box className={classes.chipContainer}>
          {consultantById.contracts.map(
            (el) =>
              !el.startDate ||
              (!!el.endDate && (
                <Chip
                  key={el.id}
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={
                    <Typography variant="subtitle1" style={{ color: Color.Body }}>
                      {locale.From}: {moment(el.startDate).format(dateFormat)} {locale.To}
                      : {moment(el.endDate).format(dateFormat)}
                    </Typography>
                  }
                  style={{
                    backgroundColor: transparentize(0.85, Color.PrimaryMain),
                    borderColor: Color.PrimaryMain,
                    color: Color.Body
                  }}
                />
              ))
          )}
        </Box>

        {consultantById.bookingStatus === ConsultantBookingStatus.NeverBooked ? (
          <Item
            title="Status"
            value={`${locale.consultantBookingStatus[consultantById.bookingStatus]} - ${
              consultantById.leadStatus
                ? locale.consultantLeadStatus[
                    consultantById.leadStatus.status
                  ].toLowerCase()
                : ""
            }`}
            iconRight={
              <IconButton
                style={{
                  verticalAlign: "bottom",
                  padding: "0px",
                  marginLeft: "4px"
                }}
                onClick={() => setLeadModalOpen(true)}
              >
                <EditFilledIcon />
              </IconButton>
            }
          />
        ) : (
          <Item
            title="Status"
            value={locale.consultantBookingStatus[consultantById.bookingStatus]}
          />
        )}
        <Item
          canCopy={false}
          title={locale.pipelineStatus}
          value={
            <PipelineStatus
              consultantId={consultantId}
              pipelineStatus={consultantById.pipelineStatus}
            />
          }
        />
        <Item
          canCopy={false}
          title={locale.QualityRate}
          value={
            <>
              <span className={cn(classes.qualityIconWrap, classes.qualityApprovalIcon)}>
                <QualityApprovalTooltip
                  qualityApprovals={consultantById.qualityApprovals}
                  rootIcon={<VerifiedIcon />}
                />
              </span>
              {professions.includesNationalProfession(consultantById.Professions) &&
              consultantById.nationalQualification ? (
                <span style={{ verticalAlign: "bottom", width: "20px" }}>
                  <QualityApprovalTooltip
                    qualityApprovals={consultantById.nationalQualityApprovals}
                  >
                    <span>
                      <NationalQualityStatusIcon
                        qualification={consultantById.nationalQualification}
                      />
                    </span>
                  </QualityApprovalTooltip>
                </span>
              ) : null}
            </>
          }
        />

        <Box>
          <Typography variant="h6" component="span">
            {locale["Works via competitors"]}:{" "}
          </Typography>
          <Typography variant="body1" component="span">
            {consultantById.worksForCompetitors.slice(0, 5).join(", ")}
          </Typography>
          <Tooltip
            title={consultantById.worksForCompetitors.join(", ")}
            placement="top-end"
            style={{ margin: "0.75rem 0" }}
            interactive
          >
            <Typography variant="body1" component="span" color="textSecondary">
              {consultantById.worksForCompetitors.length > 5
                ? ` +${consultantById.worksForCompetitors.slice(6, -1).length}`
                : null}
            </Typography>
          </Tooltip>
        </Box>

        <Item
          title={locale.lastBookedWeek}
          value={
            consultantById.bookedPeriodEnd &&
            `V.${moment(consultantById.bookedPeriodEnd).utc().format("WW YYYY")}`
          }
        />

        {consultantById?.karens && consultantById?.karens?.length > 0 ? (
          consultantById?.karens?.length === 1 ? (
            <Item
              title={locale.Karens}
              value={`${locale.Exp} ${
                consultantById.karens?.[0]?.expDate.split("T")[0]
              }, ${consultantById.karens?.[0]?.region}`}
              iconRight={
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {
                    setOpenKarensModal(true);
                  }}
                >
                  <EditFilledIcon />
                </IconButton>
              }
            />
          ) : (
            <>
              <Typography
                variant="h6"
                component="div"
                className={classes.flexSpaceBetween}
              >
                {locale.Karens}:{" "}
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {
                    setOpenKarensModal(true);
                  }}
                >
                  <EditFilledIcon />
                </IconButton>
              </Typography>
              <Box className={classes.chipContainer}>
                {consultantById.karens.map((el, index) => (
                  <Chip
                    key={index}
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={
                      <Typography variant="subtitle1" style={{ color: Color.Body }}>
                        {locale.Exp}: {el.expDate.split("T")[0]}, {el.region}
                      </Typography>
                    }
                    style={{
                      backgroundColor: transparentize(0.85, Color.PrimaryMain),
                      borderColor: Color.PrimaryMain,
                      color: Color.Body
                    }}
                  />
                ))}
              </Box>
            </>
          )
        ) : (
          <Button
            variant="text"
            color="primary"
            className={classes.karensButton}
            onClick={() => {
              setOpenKarensModal(true);
            }}
          >
            <EditFilledIcon />
            <Typography variant="body1">{locale.RegisterKarens}</Typography>
          </Button>
        )}
        <KarensModal
          open={openKarensModal}
          setOpen={setOpenKarensModal}
          karens={consultantById?.karens ?? []}
          consultantId={consultantById._id}
        />
      </Box>
    </>
  );

  const companySection =
    consultantById.LegalPersonType === "Subcontractor" ? (
      <>
        <Divider className={classes.divider} />
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.sectionTitle}
        >
          {locale.companyInformation}
        </Typography>
        <Box className={classes.container}>
          <Item title={locale.company} value={consultantById.BusinessName} canCopy />
          <Item
            title={locale["Organisation number short"]}
            value={consultantById.BusinessSocialSecurityNo}
            canCopy
          />
          <Item
            title={locale.Address}
            value={consultantById.businessAddress?.formattedShort}
            canCopy
          />
        </Box>
      </>
    ) : null;

  return loading ? (
    <Card>
      <Loader />
    </Card>
  ) : (
    <Card
      style={{
        overflow: "visible",
        backgroundColor: consultantById.Unfit
          ? transparentize(0.88, Color.ErrorLight)
          : undefined
      }}
    >
      {contactInfoSection}
      {personalInfoSection}
      {professionInfoSection}
      {bookingStatusSection}
      <WorkStatusSection consultantId={consultantId} />
      {companySection}

      <Divider className={classes.divider} />
      <ConsultantTags consultantId={consultantId} />
      <LeadStatusModal
        consultantId={consultantId}
        type="default"
        open={leadModalOpen}
        setOpen={setLeadModalOpen}
      />
    </Card>
  );
}

const useStyles = makeStyles((theme) => ({
  divider: {
    margin: "1rem 0"
  },
  avatar: {
    backgroundColor: Color.PrimaryLight,
    color: Color.Body
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px"
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "0.25rem"
  },
  avatarContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    marginBottom: "1rem"
  },
  itemIcon: {
    verticalAlign: "bottom",
    marginRight: "4px"
  },
  sectionTitle: {
    marginBottom: "0.75rem",
    textTransform: "uppercase"
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem"
  },
  karensButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    boxShadow: "none",
    padding: 0,
    gap: "0.25rem"
  },
  flexSpaceBetween: {
    display: "flex",
    justifyContent: "space-between"
  },
  iconButton: {
    padding: 0
  },
  qualityApprovalIcon: {
    margin: "4px"
  },
  qualityIconWrap: {
    "& svg": {
      verticalAlign: "bottom",
      width: 20
    }
  }
}));
